'use client'

import { cn } from '@/utilities/ui'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { Check, Minus } from 'lucide-react'
import * as React from 'react'

/**
 * Wilson Property canonical Checkbox.
 *
 * Figma spec:
 *   Checkbox (2198:36334): size 16 (size-4), rounded sm (2px), 1px theme/border
 *     (#e0dfe0), white fill, shadow/xs.
 *   Checked (2198:36335): bg theme/primary #000 + 2px #000 border, 14px white
 *     check (icon/icon-secondary).
 *   Checked secondary (2198:36337): transparent fill, 2px theme/border, 14px
 *     black check (icon/icon-primary).
 *   Indeterminate (2198:36367 / 36370 / 36373): same fill/border as checked,
 *     Lucide `minus` glyph (2193:22041) at 14px in icon/icon-secondary.
 *   Focus (2198:36358): theme/ring (#8cc64080) at 3px spread.
 *   Error (2198:36361 / 36346): theme/destructive #ef4444 border (1px unchecked,
 *     2px + destructive fill when checked).
 *   Error focus (2198:36364 / 36349): same, plus a 3px color/status/error-300
 *     (#fca5a5) ring.
 *   Disabled (2198:36355 / 36340 / 36370): 50% opacity.
 *
 * NOTE: color/status/error-300 (#fca5a5) has no entry in globals.css `@theme`
 * yet, so the error focus ring is the only literal hex here. Swap it for the
 * token once `--color-error-300` lands.
 */

const Checkbox: React.FC<
  React.ComponentProps<typeof CheckboxPrimitive.Root> & {
    /** `secondary` = Figma's outline check (transparent fill, black glyph). */
    variant?: 'default' | 'secondary'
  }
> = ({ className, variant = 'default', ...props }) => (
  <CheckboxPrimitive.Root
    data-slot="checkbox"
    className={cn(
      'peer border-border bg-white ring-ring outline-ring/50 size-4 shrink-0 rounded-sm border shadow-xs transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
      // Checked / indeterminate fill
      variant === 'secondary'
        ? 'data-[state=checked]:border-2 data-[state=checked]:border-border data-[state=checked]:bg-transparent data-[state=checked]:text-foreground data-[state=indeterminate]:border-2 data-[state=indeterminate]:border-border data-[state=indeterminate]:bg-transparent data-[state=indeterminate]:text-foreground'
        : 'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-2 data-[state=checked]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground data-[state=indeterminate]:border-2 data-[state=indeterminate]:border-primary',
      // Error + error focus. The stacked attribute selectors outrank the plain
      // data-[state] rules above, so the destructive fill always wins.
      'aria-invalid:border-destructive aria-invalid:data-[state=checked]:border-destructive aria-invalid:data-[state=checked]:bg-destructive aria-invalid:data-[state=checked]:text-primary-foreground aria-invalid:data-[state=indeterminate]:border-destructive aria-invalid:data-[state=indeterminate]:bg-destructive aria-invalid:data-[state=indeterminate]:text-primary-foreground aria-invalid:focus-visible:ring-[#fca5a5]',
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      data-slot="checkbox-indicator"
      className="group flex items-center justify-center text-current"
    >
      <Check className="size-3.5 group-data-[state=indeterminate]:hidden" />
      <Minus className="hidden size-3.5 group-data-[state=indeterminate]:block" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
)

export { Checkbox }
